import {Form, Input, Select, Button, Row, Col} from 'antd';
import {MinusCircleOutlined, PlusOutlined} from '@ant-design/icons';
import {useMutation} from '@apollo/client';
import {ADD_QUESTION_TO_MINI_FEEDBACK} from '../../resolvers';
import {TRANSLATE_TEXT} from 'graphql/mutations/notifications';
import {toast} from 'react-toastify';
import SubmitButton from 'components/Startup/components/SubmitButton';
import {Button as DabaButton} from '@dabafinance/react-components';

const QUESTION_TYPES = [
  {label: 'Multiple Select Options', value: 'OPTION'},
  {label: 'Single Select Option', value: 'RADIO'},
  {label: 'Input Text', value: 'TEXT'},
  {label: 'Input Number', value: 'NUMBER'},
  {label: 'Select Date & Time', value: 'DATETIME'},
  {label: 'Upload File', value: 'FILE'},
];

const buildMultilangArray = (q, translationLang) => {
  const translatedQuestionText = q?.multilang?.[translationLang]?.questionText;
  const hasTranslatedOptions =
    ['RADIO', 'OPTION', 'DROPDOWN'].includes(q?.questionType) &&
    (q?.options || []).some(opt => opt?.labelTranslated?.trim());
  if (!translatedQuestionText?.trim() && !hasTranslatedOptions) return null;
  const translatedItem = {
    language: translationLang,
    questionText: translatedQuestionText?.trim() || null,
  };
  if (hasTranslatedOptions) {
    translatedItem.options = (q.options || [])
      .filter(opt => opt?.label != null)
      .map(opt => ({
        label: opt.labelTranslated?.trim() || opt.label,
        value: opt.label
          ? opt.label.trim().toUpperCase().replace(/\s+/g, '_')
          : '',
      }));
  }
  return [translatedItem];
};

export const AddQuestionsForm = ({
  feedbackId,
  closeModal,
  refetch,
  baseLanguage = 'EN',
}) => {
  const [addQuestions, {loading}] = useMutation(ADD_QUESTION_TO_MINI_FEEDBACK);
  const [translateText, {loading: loadingTranslation}] =
    useMutation(TRANSLATE_TEXT);
  const [form] = Form.useForm();
  const normalizedBaseLanguage = baseLanguage === 'FR' ? 'FR' : 'EN';
  const translationLang = normalizedBaseLanguage === 'EN' ? 'FR' : 'EN';
  const baseLanguageLabel =
    normalizedBaseLanguage === 'EN' ? 'English' : 'French';
  const translationLanguageLabel =
    normalizedBaseLanguage === 'EN' ? 'French' : 'English';

  const handleTranslateQuestion = async name => {
    const sourceText = form.getFieldValue(['questions', name, 'questionText']);
    if (!sourceText?.trim()) {
      toast.error(`Please enter question text in ${baseLanguageLabel} first`);
      return;
    }
    try {
      const {data} = await translateText({
        variables: {
          sourceLang: normalizedBaseLanguage,
          targetLang: translationLang,
          sourceText,
        },
      });
      if (
        data?.translateText?.statusCode === 400 ||
        data?.translateText?.__typename === 'Error'
      ) {
        toast.error(data?.translateText?.message || 'Translation failed');
      } else {
        const translated = data?.translateText?.targetText || '';
        form.setFieldValue(
          ['questions', name, 'multilang', translationLang, 'questionText'],
          translated,
        );
        toast.success('Translated successfully');
      }
    } catch (err) {
      toast.error(err?.message || 'Translation failed');
    }
  };

  const handleTranslateOptions = async (questionName, optionIndex) => {
    const options =
      form.getFieldValue(['questions', questionName, 'options']) || [];
    const opt = options[optionIndex];
    if (!opt?.label?.trim()) {
      toast.error(
        `Please enter the option label in ${baseLanguageLabel} first`,
      );
      return;
    }
    try {
      const {data} = await translateText({
        variables: {
          sourceLang: normalizedBaseLanguage,
          targetLang: translationLang,
          sourceText: opt.label,
        },
      });
      if (
        data?.translateText?.statusCode === 400 ||
        data?.translateText?.__typename === 'Error'
      ) {
        toast.error(data?.translateText?.message || 'Translation failed');
      } else {
        const translated = data?.translateText?.targetText || '';
        const newOptions = [...options];
        newOptions[optionIndex] = {
          ...newOptions[optionIndex],
          labelTranslated: translated,
        };
        form.setFieldValue(['questions', questionName, 'options'], newOptions);
        toast.success('Translated successfully');
      }
    } catch (err) {
      toast.error(err?.message || 'Translation failed');
    }
  };

  const onFinish = values => {
    const questions = (values.questions || []).map(q => {
      const questionData = {
        questionText: q.questionText,
        questionType: q.questionType,
      };

      if (['RADIO', 'OPTION', 'DROPDOWN'].includes(q.questionType)) {
        questionData.options = (q.options || []).map(opt => ({
          label: opt.label,
          value: opt.label
            ? opt.label.trim().toUpperCase().replace(/\s+/g, '_')
            : '',
        }));
      }

      const multilang = buildMultilangArray(q, translationLang);
      if (multilang?.length) {
        questionData.multilang = multilang;
      }

      return questionData;
    });

    addQuestions({
      variables: {
        input: {
          feedbackId,
          questions,
        },
      },
    })
      .then(({data}) => {
        if (data?.addMiniFeedbackQuestions?.__typename === 'Error') {
          toast.error(data.addMiniFeedbackQuestions.message);
        } else {
          toast.success(
            data?.addMiniFeedbackQuestions?.message ||
              'Questions added successfully!',
          );
          form.resetFields();
          refetch();
          closeModal();
        }
      })
      .catch(err => {
        toast.error(err.message || 'An error occurred');
      });
  };

  return (
    <Form
      form={form}
      layout="vertical"
      style={{maxHeight: '70vh', overflowY: 'auto', paddingRight: '10px'}}
      onFinish={onFinish}
      initialValues={{
        questions: [
          {
            multilang: {[translationLang]: {questionText: '', options: []}},
          },
        ],
      }}>
      <h1 style={{fontSize: 24, marginBottom: 20}}>Add Questions</h1>

      <Form.List name="questions">
        {(fields, {add, remove}) => (
          <>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
                gap: '24px',
                marginBottom: '24px',
              }}>
              {fields.map(({key, name, ...restField}) => (
                <div
                  key={key}
                  style={{
                    background: '#f9fafb',
                    border: '1px solid #e5e7eb',
                    padding: '24px',
                    borderRadius: 12,
                    display: 'flex',
                    flexDirection: 'column',
                  }}>
                  <Row gutter={16}>
                    <Col flex="1">
                      <Form.Item
                        {...restField}
                        name={[name, 'questionText']}
                        label={`Question Text (${normalizedBaseLanguage})`}
                        rules={[
                          {
                            required: true,
                            message: 'Question text is required',
                          },
                        ]}>
                        <Input
                          placeholder={`Enter question text in ${baseLanguageLabel}`}
                        />
                      </Form.Item>
                    </Col>
                    <Col style={{paddingTop: 30}}>
                      <DabaButton
                        type="secondary"
                        label={
                          loadingTranslation ? 'Translating...' : 'Translate'
                        }
                        disabled={loadingTranslation}
                        onClick={() => handleTranslateQuestion(name)}
                      />
                    </Col>
                  </Row>

                  <Form.Item
                    {...restField}
                    name={[name, 'multilang', translationLang, 'questionText']}
                    label={`Question Text (${translationLang})`}>
                    <Input
                      placeholder={`Enter or translate to ${translationLanguageLabel}`}
                    />
                  </Form.Item>

                  <Form.Item
                    {...restField}
                    name={[name, 'questionType']}
                    label="Question Type"
                    rules={[
                      {
                        required: true,
                        message: 'Question type is required',
                      },
                    ]}>
                    <Select placeholder="Select a question type">
                      {QUESTION_TYPES.map(type => (
                        <Select.Option key={type.value} value={type.value}>
                          {type.label}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>

                  <Form.Item
                    noStyle
                    shouldUpdate={(prevValues, currentValues) =>
                      prevValues.questions?.[name]?.questionType !==
                      currentValues.questions?.[name]?.questionType
                    }>
                    {() => {
                      const type = form.getFieldValue([
                        'questions',
                        name,
                        'questionType',
                      ]);
                      return ['RADIO', 'OPTION'].includes(type) ? (
                        <div style={{marginLeft: 20}}>
                          <p style={{marginBottom: 8}}>Options</p>
                          <Form.List
                            name={[name, 'options']}
                            rules={[
                              {
                                validator: async (_, options) => {
                                  if (!options || options.length < 1) {
                                    return global.Promise.reject(
                                      new Error(
                                        'At least one option must be added',
                                      ),
                                    );
                                  }
                                },
                              },
                            ]}>
                            {(
                              optionFields,
                              {add: addOption, remove: removeOption},
                              {errors},
                            ) => (
                              <>
                                <div
                                  style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 12,
                                    marginBottom: 16,
                                  }}>
                                  {optionFields.map((optionField, oIdx) => (
                                    <div
                                      key={optionField.key}
                                      style={{
                                        display: 'flex',
                                        flexWrap: 'wrap',
                                        gap: 8,
                                        alignItems: 'flex-end',
                                      }}>
                                      <Form.Item
                                        {...optionField}
                                        label={`Label (${normalizedBaseLanguage})`}
                                        style={{
                                          minWidth: 180,
                                          margin: 0,
                                          flex: 1,
                                        }}
                                        name={[optionField.name, 'label']}
                                        rules={[
                                          {
                                            required: true,
                                            message: 'Missing label',
                                          },
                                        ]}>
                                        <Input
                                          placeholder={`Enter option in ${baseLanguageLabel}`}
                                        />
                                      </Form.Item>
                                      <Form.Item
                                        {...optionField}
                                        label={`Label (${translationLang})`}
                                        style={{
                                          minWidth: 180,
                                          margin: 0,
                                          flex: 1,
                                        }}
                                        name={[
                                          optionField.name,
                                          'labelTranslated',
                                        ]}>
                                        <Input
                                          placeholder={`Enter option in ${translationLanguageLabel}`}
                                        />
                                      </Form.Item>
                                      <div
                                        style={{
                                          display: 'flex',
                                          alignItems: 'center',
                                          height: 32,
                                        }}>
                                        <DabaButton
                                          type="secondary"
                                          label="Translate"
                                          disabled={loadingTranslation}
                                          onClick={() =>
                                            handleTranslateOptions(name, oIdx)
                                          }
                                        />
                                      </div>
                                      <div
                                        style={{
                                          display: 'flex',
                                          alignItems: 'center',
                                          height: 32,
                                        }}>
                                        <MinusCircleOutlined
                                          style={{
                                            color: '#ef4444',
                                            fontSize: 18,
                                            cursor: 'pointer',
                                          }}
                                          onClick={() =>
                                            removeOption(optionField.name)
                                          }
                                        />
                                      </div>
                                    </div>
                                  ))}
                                </div>
                                <Form.Item>
                                  <Button
                                    type="dashed"
                                    onClick={() => addOption()}
                                    block
                                    style={{
                                      borderColor: '#8b5cf6',
                                      color: '#8b5cf6',
                                    }}
                                    icon={<PlusOutlined />}>
                                    Add Option
                                  </Button>
                                  <Form.ErrorList errors={errors} />
                                </Form.Item>
                              </>
                            )}
                          </Form.List>
                        </div>
                      ) : null;
                    }}
                  </Form.Item>

                  {fields.length > 1 ? (
                    <Button
                      type="text"
                      danger
                      onClick={() => remove(name)}
                      icon={<MinusCircleOutlined />}>
                      Remove Question
                    </Button>
                  ) : null}
                </div>
              ))}
            </div>
            <Form.Item>
              <Button
                type="dashed"
                onClick={() =>
                  add({
                    multilang: {
                      [translationLang]: {questionText: '', options: []},
                    },
                  })
                }
                block
                style={{
                  height: 48,
                  borderRadius: 8,
                  borderColor: '#8b5cf6',
                  color: '#8b5cf6',
                }}
                icon={<PlusOutlined />}>
                Add Another Question
              </Button>
            </Form.Item>
          </>
        )}
      </Form.List>

      <div style={{display: 'flex', justifyContent: 'flex-end', marginTop: 30}}>
        <SubmitButton label="Submit" loading={loading} disabled={loading} />
      </div>
    </Form>
  );
};
